import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForwardRounded, LockRounded, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { editionConfig } from '../edition';
import { getCurrentUser, login } from '../features/account/api';

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturn = searchParams.get('returnTo') || '/dashboard';
  const returnTo = requestedReturn.startsWith('/') && !requestedReturn.startsWith('//') ? requestedReturn : '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getCurrentUser().then(body => {
      if (active && body.authenticated) navigate(returnTo, { replace: true });
    }).catch(() => {});
    return () => { active = false; };
  }, [navigate, returnTo]);

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await login({ email, password, remember });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="main" sx={{ position: 'fixed', inset: 0, width: '100vw', height: '100dvh', overflow: 'hidden', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Card component="section" aria-labelledby="sign-in-title" sx={{ width: '100%', maxWidth: 458, p: { xs: 2.5, sm: 4 }, borderRadius: 4, boxShadow: '0 24px 70px rgba(16, 42, 67, 0.12)' }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: 3, bgcolor: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 10px 28px rgba(26,57,130,.14)' }}><Logo brand="platform" size={46} /></Box>
          <Typography sx={{ fontWeight: 800, fontSize: 20, color: 'text.primary' }}>{editionConfig.platformName}</Typography>
          <Typography variant="caption" color="text.secondary">One secure workspace for your legal practice</Typography>
        </Stack>
        <Typography id="sign-in-title" variant="h3" align="center">Sign in to Lex Maple</Typography>
        <Typography color="text.secondary" align="center" sx={{ mt: 0.65, mb: 2.4 }}>Use the individual account provided by your firm.</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={submit} noValidate>
          <Stack spacing={1.55}>
            <TextField label="Work email" type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="username" autoFocus required fullWidth />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)} edge="end">{showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}</IconButton></InputAdornment> }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormControlLabel control={<Checkbox size="small" checked={remember} onChange={event => setRemember(event.target.checked)} />} label={<Typography variant="body2">Keep me signed in</Typography>} />
              <Link component={RouterLink} to="/recover" variant="body2">Forgot password?</Link>
            </Stack>
            <Button type="submit" variant="contained" size="large" fullWidth endIcon={<ArrowForwardRounded />} disabled={submitting || !email.includes('@') || !password} sx={{ minHeight: 50, fontSize: 16 }}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 2.2 }} />
        <Typography variant="body2" align="center" color="text.secondary">Need access? Ask your firm administrator for a personal invitation.</Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.7} sx={{ mt: 2, color: 'text.secondary' }}>
          <LockRounded sx={{ fontSize: 15 }} />
          <Typography variant="caption">Private firm workspace · individual staff access</Typography>
        </Stack>
      </Card>
    </Box>
  );
}
